<template>
  <div class="keyword-tag-editor" @click.stop>
    <div class="assistant-keyword-toolbar">
      <Input
        v-model="keywordInput"
        class="assistant-keyword-input"
        :disabled="disabled"
        :placeholder="placeholder || `多个关键词用分号（;）分割，按回车确认，每条不超过 ${keywordMaxLength} 字`"
        @on-enter="addKeywords"
      />
      <Button
        type="primary"
        class="assistant-keyword-btn"
        :disabled="disabled"
        @click="copyKeywords"
      >
        复制
      </Button>
      <Button
        class="assistant-keyword-btn assistant-keyword-btn--default"
        :disabled="disabled"
        @click="clearKeywords"
      >
        清空
      </Button>
    </div>
    <div
      v-show="hasKeywords"
      ref="keywordPanel"
      class="assistant-keyword-panel"
      :class="{ 'assistant-keyword-panel--delete-mode': keywordDeleteMode }"
      @mousedown="onKeywordPanelMouseDown"
    >
      <div class="assistant-keyword-panel__toolbar" @mousedown.stop @click.stop>
        <p
          class="assistant-keyword-panel__hint"
          :class="{ 'assistant-keyword-panel__hint--hidden': !keywordDeleteMode }"
        >
          点击标签选中，或在区域内拖拽框选，再按 Delete 或「删除选中」
        </p>
        <div class="assistant-keyword-panel__toolbar-right">
          <span class="assistant-keyword-panel__batch-del-slot">
            <Button
              v-show="keywordDeleteMode && selectedKeywordIndexes.length > 0"
              type="error"
              size="small"
              class="assistant-keyword-panel__batch-del"
              @click="batchDeleteSelectedKeywords"
            >
              删除选中 ({{ selectedKeywordIndexes.length }})
            </Button>
          </span>
          <span class="assistant-keyword-panel__del-label">删除：</span>
          <i-switch
            :value="keywordDeleteMode"
            size="small"
            :disabled="disabled"
            @on-change="onKeywordDeleteModeChange"
          />
        </div>
      </div>
      <div class="assistant-keyword-tags-wrap">
        <span
          v-for="(word, index) in keywordList"
          :key="'kw-' + index + '-' + word"
          class="assistant-keyword-tag-wrap"
          :title="word"
          :data-kw-index="index"
        >
          <Tag
            color="primary"
            class="assistant-keyword-tag"
            :class="{ 'assistant-keyword-tag--selected': selectedKeywordIndexes.includes(index) }"
            :closable="!disabled"
            @on-close="removeKeywordAt(index)"
          >
            {{ formatKeywordDisplay(word) }}
          </Tag>
        </span>
        <div
          v-show="keywordShowSelectionBox"
          class="assistant-keyword-selection-box"
          :style="keywordSelectionBoxStyle"
        />
      </div>
    </div>
  </div>
</template>

<script>
/** 单条关键词最大字数 */
const KEYWORD_MAX_LENGTH = 50

export default {
  name: 'KeywordTagEditor',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      keywordInput: '',
      keywordDeleteMode: false,
      selectedKeywordIndexes: [],
      keywordDragActive: false,
      keywordDragStartX: 0,
      keywordDragStartY: 0,
      kwDragStartClientX: 0,
      kwDragStartClientY: 0,
      kwDragMoved: false,
      kwClickIndex: null,
      keywordShowSelectionBox: false,
      keywordSelectionBoxStyle: {},
    }
  },
  computed: {
    keywordList() {
      return Array.isArray(this.value) ? this.value : []
    },
    hasKeywords() {
      return this.keywordList.length > 0
    },
    keywordMaxLength() {
      return KEYWORD_MAX_LENGTH
    },
  },
  watch: {
    value: {
      handler() {
        this.clampKeywordList()
        if (!this.hasKeywords) {
          this.resetKeywordPanelState()
        }
      },
      deep: true,
    },
    disabled(enabled) {
      if (enabled) {
        this.keywordDeleteMode = false
        this.selectedKeywordIndexes = []
        this.clearKeywordSelectionBox()
        this.teardownKeywordDragListeners()
      }
    },
  },
  mounted() {
    this.clampKeywordList()
    this._keywordKeydown = (e) => this.onKeywordKeydown(e)
    window.addEventListener('keydown', this._keywordKeydown)
  },
  beforeDestroy() {
    this.teardownKeywordDragListeners()
    if (this._keywordKeydown) {
      window.removeEventListener('keydown', this._keywordKeydown)
    }
  },
  methods: {
    resetKeywordPanelState() {
      this.keywordDeleteMode = false
      this.selectedKeywordIndexes = []
      this.clearKeywordSelectionBox()
      this.teardownKeywordDragListeners()
    },
    clampKeywordText(text) {
      const s = text != null ? String(text).trim() : ''
      if (!s) return ''
      if (s.length <= KEYWORD_MAX_LENGTH) return s
      return s.slice(0, KEYWORD_MAX_LENGTH)
    },
    formatKeywordDisplay(word) {
      const s = word != null ? String(word) : ''
      if (s.length <= KEYWORD_MAX_LENGTH) return s
      return `${s.slice(0, KEYWORD_MAX_LENGTH)}…`
    },
    clampKeywordList() {
      const list = this.keywordList
      if (!Array.isArray(list)) return
      const next = []
      let changed = false
      list.forEach((item) => {
        const raw = item != null ? String(item).trim() : ''
        const clamped = this.clampKeywordText(raw)
        if (!clamped) {
          if (raw) changed = true
          return
        }
        if (clamped !== raw || String(item) !== clamped) changed = true
        if (!next.includes(clamped)) {
          next.push(clamped)
        } else {
          changed = true
        }
      })
      if (changed || next.length !== list.length) {
        this.emitList(next)
      }
    },
    emitList(list) {
      this.$emit('input', [...list])
    },
    verifyKeywords(text) {
      let sentences = []
      try {
        sentences = text.split(/(?<![\\]);/).map((item) => item.replace(/\\;/g, ';'))
      } catch (e) {
        sentences = [text]
      }
      return sentences.reduce(
        (acc, key) => {
          const trimmed = key != null ? String(key).trim() : ''
          if (!trimmed) return acc
          try {
            new RegExp(trimmed)
            const clamped = this.clampKeywordText(trimmed)
            if (clamped.length < trimmed.length) {
              acc.truncated.push(trimmed)
            }
            acc.correct.push(clamped)
          } catch (e) {
            acc.error.push(trimmed)
          }
          return acc
        },
        { correct: [], error: [], truncated: [] },
      )
    },
    addKeywords() {
      if (this.disabled) return
      const raw = this.keywordInput != null ? String(this.keywordInput).trim() : ''
      if (!raw) return
      const { correct, error, truncated } = this.verifyKeywords(raw)
      if (error.length) {
        this.$Message.warning('部分关键词表达式格式错误，已跳过')
      }
      if (truncated.length) {
        this.$Message.warning(`部分关键词超过 ${KEYWORD_MAX_LENGTH} 字，已自动截断`)
      }
      if (!correct.length) {
        this.keywordInput = error.join(' ')
        return
      }
      const merged = [...new Set([...this.keywordList, ...correct])]
      this.emitList(merged.map((item) => this.clampKeywordText(item)).filter(Boolean))
      this.keywordInput = error.join(' ')
    },
    onKeywordDeleteModeChange(enabled) {
      if (this.disabled) return
      this.keywordDeleteMode = !!enabled
      if (!this.keywordDeleteMode) {
        this.selectedKeywordIndexes = []
        this.clearKeywordSelectionBox()
        this.teardownKeywordDragListeners()
      }
    },
    clearKeywordSelectionBox() {
      this.keywordShowSelectionBox = false
      this.keywordSelectionBoxStyle = {}
    },
    toggleKeywordIndex(index) {
      const set = new Set(this.selectedKeywordIndexes)
      if (set.has(index)) {
        set.delete(index)
      } else {
        set.add(index)
      }
      this.selectedKeywordIndexes = Array.from(set).sort((a, b) => a - b)
    },
    removeKeywordAt(index) {
      if (this.disabled) return
      const list = [...this.keywordList]
      list.splice(index, 1)
      this.emitList(list)
      if (!list.length) {
        this.resetKeywordPanelState()
      } else {
        this.selectedKeywordIndexes = this.selectedKeywordIndexes
          .filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i))
      }
    },
    onKeywordPanelMouseDown(e) {
      if (this.disabled || !this.keywordDeleteMode || e.button !== 0) return
      if (e.target.closest('.assistant-keyword-panel__toolbar')) return
      if (e.target.closest('.ivu-icon-ios-close')) return
      const panel = this.$refs.keywordPanel
      if (!panel) return
      const wrap = e.target.closest('.assistant-keyword-tag-wrap')
      this.kwClickIndex =
        wrap != null && wrap.getAttribute('data-kw-index') != null
          ? Number(wrap.getAttribute('data-kw-index'))
          : null
      this.kwDragMoved = false
      const rect = panel.getBoundingClientRect()
      this.keywordDragActive = true
      this.keywordDragStartX = e.clientX - rect.left
      this.keywordDragStartY = e.clientY - rect.top
      this.kwDragStartClientX = e.clientX
      this.kwDragStartClientY = e.clientY
      document.addEventListener('mousemove', this.onKeywordPanelMouseMove)
      document.addEventListener('mouseup', this.onKeywordPanelMouseUp)
    },
    onKeywordPanelMouseMove(e) {
      if (!this.keywordDragActive) return
      const moved =
        Math.abs(e.clientX - this.kwDragStartClientX) > 4 ||
        Math.abs(e.clientY - this.kwDragStartClientY) > 4
      if (!moved) return
      this.kwDragMoved = true
      const panel = this.$refs.keywordPanel
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      const endX = e.clientX - rect.left
      const endY = e.clientY - rect.top
      this.updateKeywordSelectionByRect(
        this.keywordDragStartX,
        this.keywordDragStartY,
        endX,
        endY,
      )
    },
    onKeywordPanelMouseUp() {
      if (!this.keywordDragActive) return
      if (!this.kwDragMoved && this.kwClickIndex != null && !Number.isNaN(this.kwClickIndex)) {
        this.toggleKeywordIndex(this.kwClickIndex)
      }
      this.keywordDragActive = false
      this.kwClickIndex = null
      this.clearKeywordSelectionBox()
      this.teardownKeywordDragListeners()
    },
    teardownKeywordDragListeners() {
      document.removeEventListener('mousemove', this.onKeywordPanelMouseMove)
      document.removeEventListener('mouseup', this.onKeywordPanelMouseUp)
      this.keywordDragActive = false
      this.clearKeywordSelectionBox()
    },
    updateKeywordSelectionByRect(startX, startY, endX, endY) {
      const panel = this.$refs.keywordPanel
      if (!panel) return
      const tagsWrap = panel.querySelector('.assistant-keyword-tags-wrap')
      const panelRect = panel.getBoundingClientRect()
      const originRect = tagsWrap ? tagsWrap.getBoundingClientRect() : panelRect
      const offsetX = originRect.left - panelRect.left
      const offsetY = originRect.top - panelRect.top
      const minX = Math.min(startX, endX) - offsetX
      const minY = Math.min(startY, endY) - offsetY
      const width = Math.abs(endX - startX)
      const height = Math.abs(endY - startY)
      this.keywordSelectionBoxStyle = {
        left: `${Math.max(0, minX)}px`,
        top: `${Math.max(0, minY)}px`,
        width: `${width}px`,
        height: `${height}px`,
      }
      this.keywordShowSelectionBox = width > 2 || height > 2
      const rect = panelRect
      const selectMinX = Math.min(startX, endX)
      const selectMinY = Math.min(startY, endY)
      const selectWidth = width
      const selectHeight = height
      const selected = []
      panel.querySelectorAll('.assistant-keyword-tag-wrap').forEach((el) => {
        const index = Number(el.getAttribute('data-kw-index'))
        if (Number.isNaN(index)) return
        const itemRect = el.getBoundingClientRect()
        const hit =
          itemRect.left - rect.left < selectMinX + selectWidth &&
          itemRect.right - rect.left > selectMinX &&
          itemRect.top - rect.top < selectMinY + selectHeight &&
          itemRect.bottom - rect.top > selectMinY
        if (hit) selected.push(index)
      })
      this.selectedKeywordIndexes = selected.sort((a, b) => a - b)
    },
    onKeywordKeydown(e) {
      if (this.disabled || !this.keywordDeleteMode || !this.selectedKeywordIndexes.length) return
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const el = e.target
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        return
      }
      e.preventDefault()
      this.batchDeleteSelectedKeywords()
    },
    batchDeleteSelectedKeywords() {
      if (this.disabled) return
      if (!this.selectedKeywordIndexes.length) {
        this.$Message.info('请先选中要删除的关键词')
        return
      }
      const removeSet = new Set(this.selectedKeywordIndexes)
      this.emitList(this.keywordList.filter((_, index) => !removeSet.has(index)))
      this.selectedKeywordIndexes = []
      this.$Message.success('已删除选中关键词')
    },
    sentencesToText(list) {
      return (list || []).map((item) => String(item).replace(/;/g, '\\;')).join(';')
    },
    copyKeywords() {
      if (this.disabled) return
      const text = this.sentencesToText(this.keywordList)
      if (!text) {
        this.$Message.info('暂无关键词可复制')
        return
      }
      const textArea = document.createElement('textarea')
      textArea.style.position = 'fixed'
      textArea.style.top = '0'
      textArea.style.left = '0'
      textArea.style.width = '2em'
      textArea.style.height = '2em'
      textArea.style.padding = '0'
      textArea.style.border = 'none'
      textArea.style.outline = 'none'
      textArea.style.boxShadow = 'none'
      textArea.style.background = 'transparent'
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        if (document.execCommand('copy')) {
          this.$Message.success('复制成功')
        }
      } catch (err) {
        // ignore
      }
      document.body.removeChild(textArea)
    },
    clearKeywords() {
      if (this.disabled) return
      this.emitList([])
      this.keywordInput = ''
      this.resetKeywordPanelState()
    },
  },
}
</script>

<style lang="scss" scoped>
.keyword-tag-editor {
  width: 100%;
  min-width: 0;
}

.assistant-keyword-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.assistant-keyword-input {
  flex: 1;
  min-width: 200px;
}

.assistant-keyword-btn {
  flex-shrink: 0;
}

::v-deep .assistant-keyword-btn.ivu-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box !important;
  height: 32px !important;
  min-height: 32px !important;
  padding: 0 15px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  border-radius: 4px !important;
}

::v-deep .assistant-keyword-btn--default.ivu-btn {
  color: #595959 !important;
  background: #fff !important;
  border: 1px solid #dcdee2 !important;
  box-shadow: none !important;

  &:hover:not(.ivu-btn-disabled),
  &:focus:not(.ivu-btn-disabled) {
    color: #2d8cf0 !important;
    border-color: #2d8cf0 !important;
    background: #fff !important;
  }

  &.ivu-btn-disabled {
    color: #c5c8ce !important;
    background: #f7f7f7 !important;
    border-color: #dcdee2 !important;
  }
}

.assistant-keyword-panel {
  margin-top: 12px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
  box-sizing: border-box;
  background-color: #f3f3f3;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  min-height: 120px;
  padding: 12px 16px 16px;
  text-align: left;
}

.assistant-keyword-panel--delete-mode {
  user-select: none;
  cursor: default;
}

.assistant-keyword-panel__toolbar {
  position: relative;
  height: 32px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #262626;
  flex-shrink: 0;
}

.assistant-keyword-panel__toolbar-right {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
}

.assistant-keyword-panel__del-label {
  line-height: 32px;
  white-space: nowrap;
}

.assistant-keyword-panel__batch-del-slot {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 118px;
  height: 32px;
}

.assistant-keyword-panel__batch-del {
  flex-shrink: 0;
}

::v-deep .assistant-keyword-panel__batch-del.ivu-btn-small {
  height: 28px;
  padding: 0 12px;
  line-height: 26px;
}

.assistant-keyword-panel__hint {
  margin: 0;
  padding-right: 230px;
  height: 32px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 32px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;

  &--hidden {
    visibility: hidden;
  }
}

.assistant-keyword-tags-wrap {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 72px;
  flex: 1;
  overflow-x: hidden;
}

.assistant-keyword-selection-box {
  position: absolute;
  border: 1px dashed #1890ff;
  background-color: rgba(24, 144, 255, 0.12);
  pointer-events: none;
  z-index: 2;
  box-sizing: border-box;
}

.assistant-keyword-tag-wrap {
  display: inline-flex;
  max-width: 480px;
  min-width: 0;
  overflow: hidden;
  vertical-align: middle;
}

.assistant-keyword-panel--delete-mode ::v-deep .assistant-keyword-tag {
  cursor: pointer;
}

::v-deep .assistant-keyword-tag.ivu-tag {
  display: inline-flex !important;
  align-items: center;
  vertical-align: middle;
  width: 100%;
  max-width: 480px;
  min-width: 0;
  overflow: hidden;
  height: 28px;
  line-height: 26px;
  padding: 0 14px !important;
  margin: 0 !important;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid #1890ff !important;
  background: #e6f7ff !important;
  color: #1890ff !important;
  box-sizing: border-box;
}

::v-deep .assistant-keyword-tag.ivu-tag .ivu-tag-text,
::v-deep .assistant-keyword-tag.ivu-tag .ivu-tag-text.ivu-tag-color-white {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 26px !important;
  color: #0050b3 !important;
}

::v-deep .assistant-keyword-tag.ivu-tag .ivu-icon-ios-close {
  flex-shrink: 0;
  margin-left: 4px;
  color: #8cbfff !important;
  opacity: 1 !important;
  font-size: 16px !important;
  font-weight: 600;
  top: 0 !important;
}

::v-deep .assistant-keyword-tag.ivu-tag .ivu-icon-ios-close:hover {
  color: #002766 !important;
}

::v-deep .assistant-keyword-tag.assistant-keyword-tag--selected.ivu-tag,
::v-deep .assistant-keyword-tag--selected.ivu-tag.ivu-tag-primary {
  background-color: #0c52e8 !important;
  border-color: #0c52e8 !important;
  color: #fff !important;
}

::v-deep .assistant-keyword-tag--selected.ivu-tag .ivu-tag-text,
::v-deep .assistant-keyword-tag--selected.ivu-tag .ivu-tag-text.ivu-tag-color-white {
  color: #fff !important;
}
</style>
